import React, { useState, useEffect } from "react";
import OptimizedImg from "../../components/OptimizedImg";

/* ============================================================
   PROJECT — "CARDFLOW", BUILT ON THE SAME SAGVEEK TECHNOLOGIES
   BRAND SYSTEM AS THE PRINTFLOW PAGE
   Product: ID Card & Student Data Generation Platform

   Same palette, type and interaction language as PrintFlow:
   Off-white page background · Near-black Deep Purple for CTAs &
   headings · Magenta/Purple accent for labels, checkmarks &
   highlights · Soft lavender panel for alternating sections.

   Palette : Off-white #FAF8FC · Lavender Panel #F5EEFA ·
             Deep Purple #3D1259 · Near-black Purple #12061F ·
             Magenta Accent #C0179E · Slate #5A4E68 · Pink #D946A8
   Type    : IBM Plex Sans (display+body) + IBM Plex Mono (data)

   Interaction pattern matches PrintFlow exactly: React-state hover
   on cards (lift + border + glow shadow), inline onMouseEnter /
   onMouseLeave handlers on buttons instead of CSS hover classes.

   NOTE: this component is written to be rendered the same way as
   the PrintFlow page — dropped directly into a products index via
   state (selectedSlug), not through react-router. It doesn't depend
   on react-router-dom; swap the <a href> links for real routes
   once routing exists.

   IMAGES: IMG_HERO, IMG_BANNER and IMG_CTA point at real screenshots
   in public/images/project3/. IMG_FORMS / IMG_TEMPLATE / IMG_PHOTOS /
   IMG_REPORTS point at real screenshots in public/images/product3/
   (5.png, 6.png, 7.png, 8.png) — the four "modern ID card operation"
   feature grid cards.

   HERO: full-bleed banner — IMG_HERO covers the entire hero section
   edge-to-edge, with a dark scrim gradient over it (not a fade into
   the page background) so the image stays fully visible and the
   copy reads in white on top of it.

   PHONE: the "Call Us" buttons (hero + CTA banner) dial CALL_PHONE
   directly via a tel: link, with an onClick fallback that forces
   the same tel: navigation on click. Update CALL_PHONE below.

   FEATURES CAROUSEL FIX (earlier revision):
   Previously visibleFeatures (5) equalled DATA.features.length (5),
   so canPrev/canNext were always false and the arrows never became
   active — and the grid track used a 5-column CSS grid that always
   filled 100% width regardless of visibleFeatures, so nothing ever
   overflowed to be scrolled to anyway. Fixed by:
     - visibleFeatures = 3 (fewer visible than total, so scrolling
       is actually possible with 5 real feature cards)
     - track switched from CSS grid to a flex row where each card's
       width is computed from visibleFeatures, so extra cards truly
       overflow the container instead of being squeezed to fit
     - translateX step matches the real card width + gap
     - dot indicators now reflect the live carouselStart instead of
       a hardcoded index

   MOBILE RESPONSIVENESS (this revision):
     - A lightweight window-width watcher (useWindowWidth) drives a
       small number of breakpoint-dependent JS values that plain CSS
       can't express here (e.g. how many feature cards are visible
       in the carousel at once).
     - Everything else — grid columns, section padding, hero layout,
       floating stat cards, the FAQ/CTA split, the dashboard mockup —
       is handled with real CSS media queries against class names
       (cf-*), so it's resilient and doesn't fight the resize watcher.
     - Breakpoints: tablet <= 900px, mobile <= 640px, small <= 420px.
     - Hero floating stat cards (Form Status / Today's Submissions)
       move from absolutely-positioned overlays into the normal flow
       below the hero copy on mobile, instead of overlapping the
       banner image or the heading.
     - The "Why choose" floating stat card is hidden below 640px
       (it has no room to float without colliding with the text).
     - The dashboard mockup's fixed sidebar collapses out of view on
       narrow screens; the metrics/status/chart grids restack.
     - The recent-submissions table scrolls horizontally on mobile
       instead of squeezing six columns into a phone width.

   UPDATE: added a shared Request-a-Demo mailto system (matching the
   Samone / HEM product pages). Both "Request a Demo +" CTAs (hero
   and CTA banner) now open a pre-filled email via DEMO_MAILTO
   instead of routing to /contact, so subject/body stay in sync if
   either is ever edited. The "Call Us" tel: buttons are unchanged.

   HERO ACCENT COLOR (this revision):
   The hero accent phrase ("ID cards, instantly.") is set to
   #6C0A9F, the brand purple used across product pages. On its own,
   that mid-tone purple has weak contrast against the dark hero
   scrim (C.ink, #12061F) — especially over darker/purple-ish parts
   of the photo. To keep #6C0A9F as the actual color while staying
   legible there, the accent span carries a thin white text-stroke
   plus a soft dark text-shadow, the same treatment used on the
   Samone product page's hero heading. Everything else on this page
   (FAQ/CTA banner links, "View all FAQs →") still uses the lighter
   pink accentOnDark (#E85FC7) for text on dark backgrounds.
   ============================================================ */

/* ─── Image slots ─── */
const IMG_HERO = "/images/project3/1.png"; // full-bleed hero banner (ID-card / student-form image)
const IMG_BANNER = "/images/project3/2.png"; // wide "why businesses choose" image (card printing / batches)
const IMG_FORMS = "/images/project3/5.png"; // small square image — "Submissions at a glance" card
const IMG_TEMPLATE = "/images/project3/6.png"; // small square image — "Card generation" card
const IMG_PHOTOS = "/images/project3/7.png"; // small square image — photo capture card
const IMG_REPORTS = "/images/project3/8.png"; // dashboard mockup + "Reports that drive growth" card
const IMG_CTA = "/images/project3/2.png"; // dark CTA banner background image — Common Questions / Ready to Automate section

/* ─── Contact number for the "Call Us" buttons ─── */
const CALL_PHONE = "+919975340919";

/* ─── Request-a-Demo mailto link (this product) ───
   Built once and reused by both "Request a Demo +" CTAs so the
   subject/body stay in sync if either is ever edited. */
const DEMO_EMAIL = "hr@sagveektechnologies.in";
const DEMO_SUBJECT = "Demo Request - CardFlow (ID Card & Student Data Platform)";
const DEMO_BODY = [
  "Hi Sagveek Technologies team,",
  "",
  "I would like to request a demo of CardFlow, the ID card & student data generation platform.",
  "",
  "Please let me know a suitable time to connect.",
  "",
  "Thanks,",
].join("\n");

const DEMO_MAILTO = `mailto:${DEMO_EMAIL}?subject=${encodeURIComponent(
  DEMO_SUBJECT
)}&body=${encodeURIComponent(DEMO_BODY)}`;

const DATA = {
  brand: "CardFlow",
  eyebrow: "All-in-One ID Card & Student Data Platform",
  headingLine1: "Turn student data into",
  headingLine2Prefix: "print-ready ",
  headingLine2Accent: "ID cards, instantly.",
  description:
    "Collect student and member data through a simple public form, then generate print-ready ID cards automatically — no spreadsheets, no manual layout work, no chasing photos by email.",
  heroBadges: ["Custom Card Templates", "Bulk Photo Processing", "Instant PDF Export", "Unlimited Clients"],
  heroFloatingTop: { label: "Form Status", value: "Open" },
  heroFloatingBottom: { label: "Today's Submissions", value: "342", delta: "+12.4% vs yesterday" },

  features: [
    { icon: "🏫", title: "Client & Form Management", desc: "Set up a new school, college or company as a client in minutes." },
    { icon: "🧩", title: "Dynamic Form Builder", desc: "Choose exactly which fields each client's students must fill in." },
    { icon: "🪪", title: "Card Template Studio", desc: "Design front & back layouts and place fields, photos & QR codes visually." },
    { icon: "📄", title: "Automated PDF Generation", desc: "Turn every submission into a print-ready card, individually or in bulk." },
    { icon: "📊", title: "Submissions & Reports", desc: "Track who's submitted, spot duplicates, and export data anytime." },
  ],

  dashboard: {
    nav: ["Dashboard", "Clients", "Students", "Templates", "Submissions", "Reports", "Alerts", "Users"],
    metrics: [
      { label: "Total Clients", value: "18" },
      { label: "Forms Open", value: "12", color: "#6C0A9F" },
      { label: "Submissions Today", value: "342" },
      { label: "Cards Generated", value: "1,284" },
    ],
    donut: { total: 18, running: 12, idle: 4, maintenance: 2 },
    weeklyOutput: [4, 5.2, 4.8, 6.5, 7.5, 7, 8.8],
    recentJobs: [
      { id: "#S1042", name: "Rahul Mehta", machine: "Greenwood High School", status: "Completed", qty: "1 card", started: "Today, 10:30 AM" },
      { id: "#S1041", name: "Ayesha Khan", machine: "Bright Minds College", status: "Running", qty: "1 card", started: "Today, 10:12 AM" },
      { id: "#S1040", name: "Devesh Naik", machine: "Sunrise Public School", status: "Pending", qty: "1 card", started: "Today, 09:58 AM" },
      { id: "#S1039", name: "Priya Verma", machine: "Greenwood High School", status: "Completed", qty: "1 card", started: "Yesterday, 04:10 PM" },
    ],
  },

  visibility: {
    title1: "Every submission,",
    title2: "instantly organized.",
    desc: "Get a single dashboard for every client you serve. Watch forms fill up in real time, catch duplicate entries before they cause reprints, and keep every client's data completely separate.",
    checklist: ["Live submission tracking, per client", "Auto-generated, isolated data tables", "Duplicate & rate-limit protection", "Role-based admin access"],
    quote: "CardFlow cut our ID card turnaround from two weeks to two days. Students fill the form themselves, and the cards come out ready to print.",
    quoteName: "Karan Deshmukh",
    quoteRole: "Director, Arihant Enterprises",
  },

  steps: [
    { n: "1", icon: "🏫", title: "Create a Client & Form", desc: "Add a school or company and choose which fields to collect." },
    { n: "2", icon: "🪪", title: "Design the Card Template", desc: "Place fields, photo and QR code on your card layout." },
    { n: "3", icon: "🔗", title: "Share the Submission Link", desc: "Students fill in their own details and upload a photo." },
    { n: "4", icon: "🖨️", title: "Auto-Generate Cards", desc: "Every submission becomes a print-ready PDF, instantly." },
    { n: "5", icon: "📈", title: "Deliver & Track", desc: "Download cards in bulk and monitor submissions over time." },
  ],

  whyChoose: {
    title1: "Why printing businesses",
    title2: "choose CardFlow",
    bullets: [
      "Eliminate manual data entry",
      "Support unlimited clients & templates",
      "Cut card turnaround time",
      "Reduce duplicate & invalid submissions",
      "Keep every client's data isolated",
    ],
    floatingStat: { label: "Turnaround Time Cut", value: "-65%", sub: "Avg. across clients" },
  },

  featureGrid: [
    {
      title: "Submissions at a glance",
      desc: "Monitor form fill-rates and data quality for every client.",
      image: IMG_FORMS,
      bullets: ["Live submission counts", "Field-level validation", "Duplicate detection", "Per-client analytics"],
    },
    {
      title: "Card generation made easy",
      desc: "Turn a filled form into a print-ready card automatically.",
      image: IMG_TEMPLATE,
      bullets: ["Template-based layout", "Multiple designs per client", "QR code support", "Batch PDF export"],
    },
    {
      title: "Photo capture that just works",
      desc: "Every ID photo comes out sharp, cropped and consistent.",
      image: IMG_PHOTOS,
      bullets: ["Auto face-friendly cropping", "Circular & rounded masks", "Format validation", "Instant preview"],
    },
    {
      title: "Reports that drive growth",
      desc: "Turn submission data into insights across every client.",
      image: IMG_REPORTS,
      bullets: ["Custom reports", "Submission trends", "Client comparisons", "Export & share"],
    },
  ],

  faq: [
    { q: "Can I manage multiple schools or clients at once?" },
    { q: "Does it support custom ID card designs?" },
    { q: "Can students submit without creating an account?" },
    { q: "Is each client's student data kept private?" },
  ],

  cta: {
    title1: "Ready to automate your",
    title2: "ID card workflow?",
    desc: "Join printing businesses and institutions already using CardFlow to collect data and generate ID cards in minutes, not weeks.",
  },
};

/* ─── Tokens (Sagveek brand: off-white / deep-purple / magenta system,
   matched 1:1 to the PrintFlow page) ─── */
const C = {
  bg: "#FAF8FC",
  surface: "#FFFFFF",
  raised: "#F5EEFA",
  border: "#E6DCEF",
  muted: "#D3B9E3",
  dimText: "#8A7B96",
  subText: "#5A4E68",
  text: "#2A2033",
  bright: "#170B2E",
  green: "#6C0A9F", // primary accent (kept the key name to match PrintFlow's token names)
  greenD: "#9E1281", // primary hover state
  greenSoft: "#F5EEFA", // soft accent panel tint
  greenGlow: "#3D125914", // card hover glow
  amber: "#D946A8", // secondary pink accent
  blue: "#3D1259", // deep purple — structural accent (icon chips, step circles)
  purple: "#8B2FC9", // mid purple
  orange: "#8B2FC9",
  ink: "#12061F", // near-black purple for dark sections
  accentOnDark: "#E85FC7", // lighter pink accent, used for text sitting on dark/ink backgrounds (hero, CTA/FAQ banner)
};

const FF = {
  display: "'IBM Plex Sans', system-ui, sans-serif",
  body: "'IBM Plex Sans', system-ui, sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

/* ─── Responsive helper: tracks viewport width so the carousel can
   decide how many cards fit on screen. Pure-CSS breakpoints (grid
   columns, stacking, hiding floating cards) are handled separately
   via the cf-* classes + media queries in the <style> block below,
   so this hook is only used for the one thing CSS can't drive. ─── */
function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    function onResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

/* ─── Small building blocks ─── */
function CheckDot({ color = C.green }) {
  return (
    <div
      style={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        background: `${color}18`,
        border: `1px solid ${color}55`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: color }} />
    </div>
  );
}

function CheckItem({ text, size = 13, light = false }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <span style={{ color: light ? "#F0A8DE" : C.green, fontSize: size + 1, lineHeight: 1, fontWeight: 700 }}>✓</span>
      <span style={{ fontFamily: FF.body, fontSize: size, color: light ? "#F1EAF6" : C.text }}>{text}</span>
    </div>
  );
}

function Pill({ children, light = false }) {
  return (
    <div
      style={{
        display: "inline-block",
        background: light ? "rgba(255,255,255,0.12)" : C.greenSoft,
        color: light ? "#fff" : C.greenD,
        fontFamily: FF.body,
        fontSize: 12.5,
        fontWeight: 600,
        padding: "7px 16px",
        borderRadius: 100,
        border: light ? "1px solid rgba(255,255,255,0.3)" : `1px solid ${C.green}30`,
        backdropFilter: light ? "blur(4px)" : "none",
      }}
    >
      {children}
    </div>
  );
}

function Section({ children, style, className = "" }) {
  return (
    <section className={`cf-section ${className}`} style={{ maxWidth: 1160, margin: "0 auto", ...style }}>
      {children}
    </section>
  );
}

function SectionHeading({ children, align = "center" }) {
  return (
    <h2
      className="cf-section-heading"
      style={{
        fontFamily: FF.display,
        fontWeight: 700,
        color: C.bright,
        margin: 0,
        letterSpacing: "-0.5px",
        lineHeight: 1.25,
        textAlign: align,
      }}
    >
      {children}
    </h2>
  );
}

/* ─── Feature carousel card — hover pattern matched to PrintFlow's
   FeatureCard: lift + border + glow shadow, icon chip inverts to
   solid deep-purple with a white icon on hover. ─── */
function FeatureCard({ icon, title, desc }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.muted : C.border}`,
        borderRadius: 14,
        padding: "22px 20px",
        transition: "all 0.2s ease",
        transform: hov ? "translateY(-3px)" : "none",
        boxShadow: hov ? `0 16px 36px ${C.greenGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
        height: "100%",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: hov ? C.blue : C.raised,
          color: hov ? "#fff" : C.blue,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          marginBottom: 16,
          transition: "all 0.2s ease",
        }}
      >
        {icon}
      </div>
      <h3 style={{ fontFamily: FF.display, fontSize: 15.5, fontWeight: 700, color: C.bright, margin: "0 0 8px" }}>{title}</h3>
      <p style={{ fontFamily: FF.body, fontSize: 13, color: C.subText, margin: "0 0 16px", lineHeight: 1.6 }}>{desc}</p>
      <a href="#learn-more" style={{ fontFamily: FF.body, fontSize: 12.5, fontWeight: 600, color: C.green, textDecoration: "none" }}>
        Learn more →
      </a>
    </div>
  );
}

/* ─── Feature grid card — same hover pattern as FeatureCard, applied
   to the "Submissions / Card generation / Photo capture / Reports" tiles. ─── */
function GridCard({ title, desc, image, bullets }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? C.raised : C.surface,
        border: `1px solid ${hov ? C.muted : C.border}`,
        borderRadius: 14,
        padding: 20,
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        transform: hov ? "translateY(-4px)" : "none",
        boxShadow: hov ? `0 16px 36px ${C.greenGlow}` : "0 1px 2px rgba(23,11,46,0.04)",
      }}
    >
      <h3 style={{ fontFamily: FF.display, fontSize: 14.5, fontWeight: 700, color: C.bright, margin: "0 0 6px" }}>{title}</h3>
      <p style={{ fontFamily: FF.body, fontSize: 12, color: C.subText, margin: "0 0 14px", lineHeight: 1.6 }}>{desc}</p>

      <div
        style={{
          borderRadius: 10,
          overflow: "hidden",
          background: C.raised,
          border: `1px solid ${C.border}`,
          aspectRatio: "4/3",
          marginBottom: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {image ? (
          <OptimizedImg src={image} alt={title} loading="lazy" pictureStyle={{ width: "100%", height: "100%", display: "block" }} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ fontFamily: FF.body, fontSize: 11, color: C.dimText }}>IMAGE</span>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {bullets.map((b) => (
          <CheckItem key={b} text={b} size={11.5} />
        ))}
      </div>
    </div>
  );
}

/* ─── FAQ row (dark CTA banner) — hover lightens the row background,
   matching the interactive feel used across the page. ─── */
function FaqRow({ q, isLast }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        padding: "12px 10px",
        margin: "0 -10px",
        borderRadius: 8,
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.14)",
        background: hov ? "rgba(255,255,255,0.06)" : "transparent",
        transition: "background 0.15s ease",
        cursor: "pointer",
      }}
    >
      <span style={{ fontFamily: FF.body, fontSize: 13, color: "#E5E7EB" }}>{q}</span>
      <span style={{ color: hov ? "#fff" : "rgba(255,255,255,0.5)", fontSize: 14, transition: "color 0.15s ease" }}>›</span>
    </div>
  );
}

/* ─── Dashboard mockup (sidebar + widgets). On mobile the fixed nav
   sidebar collapses out of view (cf-dash-sidebar) and the widget
   grids restack via CSS, so the mockup still reads as a coherent
   dashboard card instead of squeezing 150px + widgets into a phone
   width. The recent-submissions table scrolls horizontally
   (cf-dash-table-scroll) rather than truncating columns. ─── */
function DashboardMockup() {
  const d = DATA.dashboard;
  const total = d.donut.total;
  const runPct = (d.donut.running / total) * 100;
  const idlePct = (d.donut.idle / total) * 100;
  const maintPct = (d.donut.maintenance / total) * 100;
  const gradient = `conic-gradient(${C.green} 0 ${runPct}%, ${C.amber} ${runPct}% ${runPct + idlePct}%, #EF4444 ${runPct + idlePct}% ${runPct + idlePct + maintPct}%, ${C.border} ${runPct + idlePct + maintPct}% 100%)`;
  const maxOut = Math.max(...d.weeklyOutput);
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const pts = d.weeklyOutput
    .map((v, i) => `${(i / (d.weeklyOutput.length - 1)) * 100},${100 - (v / maxOut) * 90}`)
    .join(" ");

  const statusColor = { Completed: C.green, Running: C.blue, Pending: C.amber };

  return (
    <div className="cf-dash" style={{ display: "grid", gridTemplateColumns: "150px 1fr", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: `0 20px 50px ${C.greenGlow}` }}>
      {/* sidebar */}
      <div className="cf-dash-sidebar" style={{ borderRight: `1px solid ${C.border}`, background: C.raised, padding: "16px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, padding: "0 4px" }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, background: C.blue }} />
          <span style={{ fontFamily: FF.display, fontSize: 13, fontWeight: 700, color: C.bright }}>{DATA.brand}</span>
        </div>
        {d.nav.map((item, i) => (
          <div
            key={item}
            style={{
              fontFamily: FF.body,
              fontSize: 11.5,
              padding: "8px 10px",
              borderRadius: 7,
              marginBottom: 2,
              color: i === 0 ? C.greenD : C.subText,
              background: i === 0 ? C.greenSoft : "transparent",
              fontWeight: i === 0 ? 600 : 500,
            }}
          >
            {item}
          </div>
        ))}
      </div>

      {/* main */}
      <div style={{ padding: "18px 20px", minWidth: 0 }}>
        <div style={{ fontFamily: FF.display, fontSize: 14, fontWeight: 700, color: C.bright, marginBottom: 14 }}>Dashboard</div>

        <div className="cf-dash-metrics" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 16 }}>
          {d.metrics.map((m) => (
            <div key={m.label}>
              <div style={{ fontFamily: FF.body, fontSize: 10, color: C.dimText, marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontFamily: FF.display, fontSize: 17, fontWeight: 700, color: m.color || C.bright }}>{m.value}</div>
            </div>
          ))}
        </div>

        <div className="cf-dash-widgets" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12, marginBottom: 16 }}>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.dimText, marginBottom: 10 }}>Client Form Status</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 62,
                  height: 62,
                  borderRadius: "50%",
                  background: gradient,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontFamily: FF.display, fontSize: 13, fontWeight: 700, color: C.bright }}>{total}</span>
                  <span style={{ fontFamily: FF.body, fontSize: 7, color: C.dimText }}>Total</span>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {[
                  { label: "Open", v: d.donut.running, color: C.green },
                  { label: "Draft", v: d.donut.idle, color: C.amber },
                  { label: "Closed", v: d.donut.maintenance, color: "#EF4444" },
                ].map((r) => (
                  <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: r.color }} />
                    <span style={{ fontFamily: FF.body, fontSize: 9.5, color: C.subText }}>
                      {r.label} {Math.round((r.v / total) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
            <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.dimText, marginBottom: 8 }}>Submissions Overview (This Week)</div>
            <svg viewBox="0 0 100 60" style={{ width: "100%", height: 60, display: "block" }} preserveAspectRatio="none">
              <polyline points={pts} fill="none" stroke={C.green} strokeWidth="2" vectorEffect="non-scaling-stroke" />
            </svg>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {days.map((d2) => (
                <span key={d2} style={{ fontFamily: FF.body, fontSize: 8, color: C.dimText }}>
                  {d2}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="cf-dash-table-scroll" style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
          <div style={{ minWidth: 460 }}>
            <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.dimText, padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>Recent Submissions</div>
            <div style={{ display: "grid", gridTemplateColumns: "56px 1fr 120px 70px 60px 90px", gap: 6, padding: "6px 14px", fontFamily: FF.mono, fontSize: 8.5, color: C.dimText, letterSpacing: "0.4px" }}>
              <span>STUDENT ID</span>
              <span>NAME</span>
              <span>CLIENT</span>
              <span>STATUS</span>
              <span>CARDS</span>
              <span>SUBMITTED AT</span>
            </div>
            {d.recentJobs.map((j) => (
              <div
                key={j.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "56px 1fr 120px 70px 60px 90px",
                  gap: 6,
                  padding: "7px 14px",
                  borderTop: `1px solid ${C.border}`,
                  alignItems: "center",
                }}
              >
                <span style={{ fontFamily: FF.mono, fontSize: 9, color: C.dimText }}>{j.id}</span>
                <span style={{ fontFamily: FF.body, fontSize: 10, color: C.text }}>{j.name}</span>
                <span style={{ fontFamily: FF.body, fontSize: 9.5, color: C.subText }}>{j.machine}</span>
                <span style={{ fontFamily: FF.body, fontSize: 9.5, color: statusColor[j.status] || C.subText, fontWeight: 600 }}>{j.status}</span>
                <span style={{ fontFamily: FF.body, fontSize: 9.5, color: C.subText }}>{j.qty}</span>
                <span style={{ fontFamily: FF.body, fontSize: 9, color: C.dimText }}>{j.started}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Page ─── */
const ProjectOneProductDetailPage = () => {
  const [carouselStart, setCarouselStart] = useState(0);
  const [prevHov, setPrevHov] = useState(false);
  const [nextHov, setNextHov] = useState(false);

  const width = useWindowWidth();
  const isTablet = width <= 900;
  const isMobile = width <= 640;

  // Fewer visible cards as the viewport narrows: 3 on desktop, 2 on
  // tablet, 1 on phones — always fewer than the total (5) so the
  // carousel always has something real to scroll to.
  const visibleFeatures = isMobile ? 1 : isTablet ? 2 : 3;
  const gapPx = 16;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const maxStart = Math.max(0, DATA.features.length - visibleFeatures);

  // Keep the carousel position valid when visibleFeatures shrinks/grows
  // on resize (e.g. rotating a tablet, or resizing a desktop window).
  useEffect(() => {
    setCarouselStart((s) => Math.min(s, maxStart));
  }, [maxStart]);

  const canPrev = carouselStart > 0;
  const canNext = carouselStart < maxStart;

  // Each card's width is 100% / visibleFeatures (minus its share of the gaps),
  // and the track shifts by exactly one card-width + gap per step — so cards
  // beyond the visible window truly overflow the container instead of being
  // squeezed to fit.
  const cardBasis = `calc((100% - ${gapPx * (visibleFeatures - 1)}px) / ${visibleFeatures})`;
  const translatePerStep = `calc(${cardBasis} + ${gapPx}px)`;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .cf-root, .cf-root *{ box-sizing:border-box; }

        /* ── Base section rhythm; tightened progressively on smaller screens ── */
        .cf-section{ padding:72px 40px; }
        .cf-section-heading{ font-size:clamp(24px, 3vw, 32px); }

        /* ── Hero ── */
        .cf-hero-wrap{ position:relative; min-height:92vh; display:flex; align-items:center; overflow:hidden; }
        .cf-hero-inner{ position:relative; z-index:2; max-width:1160px; margin:0 auto; padding:88px 40px 64px; width:100%; }
        .cf-hero-heading{ font-family:${FF.display}; font-weight:800; font-size:clamp(32px, 4vw, 44px); line-height:1.15; letter-spacing:-1px; color:#fff; margin:0 0 16px; max-width:560px; text-shadow:0 2px 12px rgba(0,0,0,0.45); }
        .cf-hero-badges{ display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
        .cf-hero-floating{ display:flex; flex-direction:column; gap:14px; margin-top:28px; }
        .cf-hero-float-card{ position:absolute; z-index:3; background:${C.surface}; border:1px solid ${C.border}; border-radius:12px; box-shadow:0 12px 30px rgba(23,11,46,0.35); }

        /* ── Feature grid (4-tile "modern ID card operation" section) ── */
        .cf-feature-grid{ display:grid; grid-template-columns:repeat(4, 1fr); gap:18px; }

        /* ── Dashboard mockup ── */
        .cf-dash-metrics{ display:grid; grid-template-columns:repeat(4, 1fr); gap:10px; }
        .cf-dash-widgets{ display:grid; grid-template-columns:1fr 1.4fr; gap:12px; }
        .cf-dash-table-scroll{ overflow-x:auto; -webkit-overflow-scrolling:touch; }

        /* ── Visibility (dashboard image + copy) split ── */
        .cf-visibility-grid{ display:grid; grid-template-columns:1.3fr 1fr; gap:40px; align-items:start; }

        /* ── 5-step grid ── */
        .cf-steps-grid{ position:relative; display:grid; grid-template-columns:repeat(5, 1fr); gap:12px; }
        .cf-steps-line{ position:absolute; top:22px; left:10%; right:10%; border-top:2px dashed ${C.border}; z-index:0; }

        /* ── FAQ + CTA split ── */
        .cf-faqcta-grid{ display:grid; grid-template-columns:0.8fr 1.5fr; gap:24px; align-items:stretch; }

        /* ================= TABLET (<=900px) ================= */
        @media (max-width:900px){
          .cf-feature-grid{ grid-template-columns:repeat(2, 1fr); }
          .cf-visibility-grid{ grid-template-columns:1fr; gap:32px; }
          .cf-dash-widgets{ grid-template-columns:1fr; }
          .cf-steps-grid{ grid-template-columns:repeat(3, 1fr); row-gap:28px; }
          .cf-steps-line{ display:none; }
          .cf-faqcta-grid{ grid-template-columns:1fr; }
        }

        /* ================= MOBILE (<=640px) ================= */
        @media (max-width:640px){
          .cf-section{ padding:48px 20px; }
          .cf-hero-wrap{ min-height:0; }
          .cf-hero-inner{ padding:120px 20px 40px; }
          .cf-hero-heading{ max-width:100%; }
          .cf-hero-float-card{ position:static !important; inset:auto !important; top:auto !important; right:auto !important; bottom:auto !important; left:auto !important; margin-top:12px; box-shadow:0 6px 18px rgba(23,11,46,0.18) !important; }
          .cf-whychoose-float{ display:none; }
          .cf-whychoose-copy{ max-width:100% !important; }
          .cf-feature-grid{ grid-template-columns:1fr; }
          .cf-dash-metrics{ grid-template-columns:repeat(2, 1fr); }
          .cf-dash{ grid-template-columns:1fr !important; }
          .cf-dash-sidebar{ display:none; }
          .cf-steps-grid{ grid-template-columns:repeat(2, 1fr); }
          .cf-recent-jobs-label{ font-size:9px; }
        }

        /* ================= SMALL PHONES (<=420px) ================= */
        @media (max-width:420px){
          .cf-steps-grid{ grid-template-columns:1fr; }
          .cf-hero-inner{ padding-top:104px; }
        }
      `}</style>

      <div className="cf-root" style={{ background: C.bg, color: C.text, fontFamily: FF.body, minHeight: "100vh", overflowX: "hidden" }}>
        {/* ══ HERO — full-bleed banner image, edge-to-edge, dark scrim for readable white copy ══ */}
        <div className="cf-hero-wrap">
          {/* full-bleed background image covering the entire hero */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0, background: C.ink }}>
            <OptimizedImg
              src={IMG_HERO}
              alt="CardFlow student ID card form and card preview"
              loading="eager"
              fetchPriority="high"
              pictureStyle={{ width: "100%", height: "100%", display: "block" }}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            {/* scrim overlay for text contrast — image stays fully visible, not faded into the page bg */}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(100deg, ${C.ink}E6 0%, ${C.ink}B3 38%, ${C.ink}66 62%, ${C.ink}26 100%)` }} />
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${C.ink}CC 0%, transparent 34%)` }} />
          </div>

          <div className="cf-hero-inner">
            <div style={{ marginBottom: 20 }}>
              <Pill light>{DATA.eyebrow}</Pill>
            </div>
            <h1 className="cf-hero-heading">
              {DATA.headingLine1}
              <br />
              {DATA.headingLine2Prefix}
              <span
                style={{
                  color: "#6C0A9F",
                  WebkitTextStroke: "0.6px rgba(255,255,255,0.85)",
                  textShadow: "0 1px 12px rgba(0,0,0,0.55)",
                }}
              >
                {DATA.headingLine2Accent}
              </span>
            </h1>
            <p style={{ fontFamily: FF.body, fontSize: 15.5, color: "#D9CFE3", maxWidth: 460, margin: "0 0 28px", lineHeight: 1.7 }}>
              {DATA.description}
            </p>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 26 }}>
              <a
                href={DEMO_MAILTO}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 14.5,
                  padding: "13px 24px",
                  background: C.green,
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                  display: "inline-block",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.greenD)}
                onMouseLeave={(e) => (e.currentTarget.style.background = C.green)}
              >
                Request a Demo +
              </a>
              <a
                href={`tel:${CALL_PHONE}`}
                onClick={() => { window.location.href = `tel:${CALL_PHONE}`; }}
                style={{
                  fontFamily: FF.body,
                  fontWeight: 600,
                  fontSize: 14.5,
                  padding: "13px 24px",
                  border: "1.5px solid rgba(255,255,255,0.4)",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: 10,
                  transition: "all 0.2s",
                  display: "inline-block",
                  background: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(4px)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.14)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.7)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
                }}
              >
                Call Us
              </a>
            </div>

            <div className="cf-hero-badges">
              {DATA.heroBadges.map((t) => (
                <CheckItem key={t} text={t} size={12.5} light />
              ))}
            </div>

            {/* On mobile the two floating cards below drop into normal flow
                here (see .cf-hero-float-card media query) instead of the
                absolutely-positioned overlay used on larger screens. */}
            <div className="cf-hero-floating">
              <div className="cf-hero-float-card" style={{ top: "16%", right: "8%", padding: "12px 16px", minWidth: 130 }}>
                <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.dimText, marginBottom: 6 }}>{DATA.heroFloatingTop.label}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span style={{ color: C.green, fontSize: 12 }}>●</span>
                  <span style={{ fontFamily: FF.display, fontSize: 13, fontWeight: 700, color: C.bright }}>{DATA.heroFloatingTop.value}</span>
                </div>
                <svg viewBox="0 0 80 24" style={{ width: 80, height: 24 }}>
                  <polyline points="0,20 15,14 30,18 45,8 60,12 80,2" fill="none" stroke={C.green} strokeWidth="2" />
                </svg>
              </div>

              <div className="cf-hero-float-card" style={{ bottom: "10%", right: "6%", padding: "14px 18px" }}>
                <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.dimText, marginBottom: 6 }}>{DATA.heroFloatingBottom.label}</div>
                <div style={{ fontFamily: FF.display, fontSize: 22, fontWeight: 800, color: C.bright, marginBottom: 4 }}>{DATA.heroFloatingBottom.value}</div>
                <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.green, fontWeight: 600 }}>{DATA.heroFloatingBottom.delta}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ══ FEATURES CAROUSEL ══ */}
        <div id="features">
          <Section style={{ paddingTop: 56, paddingBottom: 56 }}>
            <div style={{ textAlign: "center", marginBottom: 36 }}>
              <SectionHeading>
                Powerful <span style={{ color: C.green }}>features</span>, built for modern ID card operations.
              </SectionHeading>
            </div>

            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 12 }}>
              <button
                type="button"
                onClick={() => canPrev && setCarouselStart((s) => Math.max(0, s - 1))}
                onMouseEnter={() => setPrevHov(true)}
                onMouseLeave={() => setPrevHov(false)}
                disabled={!canPrev}
                aria-label="Previous features"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: `1px solid ${canPrev && prevHov ? C.blue : C.border}`,
                  background: canPrev && prevHov ? C.raised : C.surface,
                  color: canPrev ? C.bright : C.dimText,
                  cursor: canPrev ? "pointer" : "default",
                  flexShrink: 0,
                  fontSize: 16,
                  opacity: canPrev ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
              >
                ‹
              </button>

              <div style={{ flex: 1, overflow: "hidden" }}>
                <div
                  style={{
                    display: "flex",
                    gap: gapPx,
                    transform: `translateX(calc(-${carouselStart} * ${translatePerStep}))`,
                    transition: "transform 0.3s ease",
                  }}
                >
                  {DATA.features.map((f, i) => (
                    <div key={i} style={{ flex: `0 0 ${cardBasis}`, minWidth: 0 }}>
                      <FeatureCard {...f} />
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => canNext && setCarouselStart((s) => Math.min(maxStart, s + 1))}
                onMouseEnter={() => setNextHov(true)}
                onMouseLeave={() => setNextHov(false)}
                disabled={!canNext}
                aria-label="Next features"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: `1px solid ${canNext && nextHov ? C.blue : C.border}`,
                  background: canNext && nextHov ? C.raised : C.surface,
                  color: canNext ? C.bright : C.dimText,
                  cursor: canNext ? "pointer" : "default",
                  flexShrink: 0,
                  fontSize: 16,
                  opacity: canNext ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
              >
                ›
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 24 }}>
              {DATA.features.map((_, i) => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: i === carouselStart ? C.green : C.border }} />
              ))}
            </div>
          </Section>
        </div>

        {/* ══ DASHBOARD MOCKUP + VISIBILITY ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.raised }}>
          <Section>
            <div className="cf-visibility-grid">
              <div
                style={{
                  borderRadius: 14,
                  overflow: "hidden",
                  border: `1px solid ${C.border}`,
                  boxShadow: `0 20px 50px ${C.greenGlow}`,
                }}
              >
                <OptimizedImg
                  src={IMG_REPORTS}
                  alt="CardFlow dashboard"
                  loading="lazy"
                  pictureStyle={{ width: "100%", height: "100%", display: "block" }}
                  style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
                />
              </div>

              <div>
                <h2
                  style={{
                    fontFamily: FF.display,
                    fontSize: "clamp(22px, 3vw, 28px)",
                    fontWeight: 700,
                    color: C.bright,
                    margin: "8px 0 16px",
                    lineHeight: 1.25,
                  }}
                >
                  {DATA.visibility.title1}
                  <br />
                  <span style={{ color: C.green }}>{DATA.visibility.title2}</span>
                </h2>
                <p style={{ fontFamily: FF.body, fontSize: 14.5, color: C.subText, lineHeight: 1.75, margin: "0 0 22px" }}>{DATA.visibility.desc}</p>

                <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 28 }}>
                  {DATA.visibility.checklist.map((c) => (
                    <CheckItem key={c} text={c} size={13.5} />
                  ))}
                </div>

                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "22px 24px" }}>
                  <div style={{ fontSize: 26, color: C.green, lineHeight: 1, marginBottom: 8 }}>❝</div>
                  <p style={{ fontFamily: FF.body, fontSize: 13.5, color: C.text, lineHeight: 1.7, margin: "0 0 16px", fontStyle: "italic" }}>{DATA.visibility.quote}</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontFamily: FF.body, fontWeight: 700, fontSize: 13, color: C.bright }}>— {DATA.visibility.quoteName}</div>
                      <div style={{ fontFamily: FF.body, fontSize: 11.5, color: C.dimText }}>{DATA.visibility.quoteRole}</div>
                    </div>
                    <div style={{ color: C.amber, fontSize: 13, letterSpacing: 1 }}>★★★★★</div>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </div>

        {/* ══ 5 STEPS ══ */}
        <Section>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <SectionHeading>
              Five steps to <span style={{ color: C.green }}>smarter ID card</span> generation.
            </SectionHeading>
          </div>

          <div className="cf-steps-grid">
            <div className="cf-steps-line" />
            {DATA.steps.map((s, i) => (
              <div key={i} style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "0 8px" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    background: C.surface,
                    border: `1.5px solid ${C.blue}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 14px",
                    fontSize: 17,
                  }}
                >
                  {s.icon}
                </div>
                <div style={{ fontFamily: FF.body, fontSize: 13, fontWeight: 700, color: C.bright, marginBottom: 6 }}>
                  {s.n}. {s.title}
                </div>
                <p style={{ fontFamily: FF.body, fontSize: 12, color: C.subText, margin: 0, lineHeight: 1.6 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ══ WHY CHOOSE — full-bleed image section ══ */}
        <div style={{ position: "relative", borderTop: `1px solid ${C.border}`, overflow: "hidden", minHeight: 460, display: "flex", alignItems: "center" }}>
          {/* full-bleed background image */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0, background: C.raised }}>
            {IMG_BANNER ? (
              <OptimizedImg src={IMG_BANNER} alt="ID card printing batch" loading="lazy" pictureStyle={{ width: "100%", height: "100%", display: "block" }} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: C.dimText, fontFamily: FF.body, fontSize: 13 }}>
                IMG_BANNER
              </div>
            )}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(100deg, ${C.bg} 28%, ${C.bg}E6 46%, ${C.bg}55 66%, transparent 90%)` }} />
          </div>

          {/* content pinned left explicitly. The outer wrapper above is display:flex, which makes
              Section shrink-to-fit and center via its auto margins unless we force it back to
              full width — width:"100%" restores the normal "fill up to maxWidth" box, and THEN
              justifyContent:flex-start pushes the inner column to the true left edge of it. */}
          <Section style={{ position: "relative", zIndex: 2, display: "flex", justifyContent: "flex-start", width: "100%" }}>
            <div className="cf-whychoose-copy" style={{ maxWidth: 400, textAlign: "left" }}>
              <h2 style={{ fontFamily: FF.display, fontSize: "clamp(20px, 2.6vw, 26px)", fontWeight: 700, color: C.bright, margin: "0 0 18px", lineHeight: 1.3 }}>
                {DATA.whyChoose.title1}
                <br />
                choose <span style={{ color: C.green }}>{DATA.whyChoose.title2.replace("choose ", "")}</span>
              </h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {DATA.whyChoose.bullets.map((b) => (
                  <CheckItem key={b} text={b} />
                ))}
              </div>
            </div>
          </Section>

          {/* floating stat — top-right, over the image; hidden on mobile
              where there's no room for it without colliding with the copy */}
          <div
            className="cf-whychoose-float"
            style={{
              position: "absolute",
              zIndex: 3,
              top: "14%",
              right: "6%",
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: "14px 18px",
              boxShadow: "0 12px 30px rgba(23,11,46,0.16)",
            }}
          >
            <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.dimText, marginBottom: 6 }}>{DATA.whyChoose.floatingStat.label}</div>
            <div style={{ fontFamily: FF.display, fontSize: 20, fontWeight: 800, color: C.green, marginBottom: 4 }}>{DATA.whyChoose.floatingStat.value}</div>
            <div style={{ fontFamily: FF.body, fontSize: 10.5, color: C.subText }}>{DATA.whyChoose.floatingStat.sub}</div>
          </div>
        </div>

        {/* ══ FEATURE GRID ══ */}
        <div style={{ borderTop: `1px solid ${C.border}`, background: C.raised }}>
          <Section>
            <div style={{ textAlign: "center", marginBottom: 40 }}>
              <SectionHeading>
                Everything you need to run a <span style={{ color: C.green }}>modern ID card operation.</span>
              </SectionHeading>
            </div>

            <div className="cf-feature-grid">
              {DATA.featureGrid.map((f, i) => (
                <GridCard key={i} {...f} />
              ))}
            </div>
          </Section>
        </div>

        {/* ══ FAQ + CTA — full-bleed image section ══ */}
        <div style={{ position: "relative", overflow: "hidden", minHeight: 420, display: "flex", alignItems: "center" }}>
          {/* full-bleed background image */}
          <div style={{ position: "absolute", inset: 0, zIndex: 0, background: C.ink }}>
            {IMG_CTA ? (
              <OptimizedImg src={IMG_CTA} alt="" loading="lazy" pictureStyle={{ width: "100%", height: "100%", display: "block" }} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.55 }} />
            ) : null}
            <div style={{ position: "absolute", inset: 0, background: `linear-gradient(100deg, ${C.ink} 30%, ${C.ink}CC 55%, ${C.ink}77 78%, ${C.ink}33 100%)` }} />
            <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 75% 50%, ${C.greenGlow} 0%, transparent 60%)` }} />
          </div>

          <Section style={{ position: "relative", zIndex: 2, width: "100%" }}>
            <div className="cf-faqcta-grid">
              {/* FAQ card */}
              <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(6px)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 16, padding: "26px 26px" }}>
                <h3 style={{ fontFamily: FF.display, fontSize: 15.5, fontWeight: 700, color: "#fff", margin: "0 0 18px" }}>Common questions</h3>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {DATA.faq.map((f, i) => (
                    <FaqRow key={i} q={f.q} isLast={i === DATA.faq.length - 1} />
                  ))}
                </div>
                <a href="#faq" style={{ display: "inline-block", marginTop: 16, fontFamily: FF.body, fontWeight: 600, fontSize: 12.5, color: C.accentOnDark, textDecoration: "none" }}>
                  View all FAQs →
                </a>
              </div>

              {/* CTA text */}
              <div style={{ display: "flex", alignItems: "center", padding: "12px 8px" }}>
                <div style={{ maxWidth: 420 }}>
                  <h2 style={{ fontFamily: FF.display, fontSize: "clamp(20px, 2.4vw, 26px)", fontWeight: 700, color: "#fff", margin: "0 0 14px", lineHeight: 1.3 }}>
                    {DATA.cta.title1}
                    <br />
                    <span style={{ color: C.accentOnDark }}>{DATA.cta.title2}</span>
                  </h2>
                  <p style={{ fontFamily: FF.body, fontSize: 13.5, color: "#CBD5E1", margin: "0 0 24px", lineHeight: 1.7 }}>{DATA.cta.desc}</p>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <a
                      href={DEMO_MAILTO}
                      style={{
                        fontFamily: FF.body,
                        fontWeight: 600,
                        fontSize: 13.5,
                        padding: "12px 22px",
                        background: C.green,
                        color: "#fff",
                        textDecoration: "none",
                        borderRadius: 8,
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = C.greenD;
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = C.green;
                        e.currentTarget.style.transform = "none";
                      }}
                    >
                      Request a Demo +
                    </a>
                    <a
                      href={`tel:${CALL_PHONE}`}
                      onClick={() => { window.location.href = `tel:${CALL_PHONE}`; }}
                      style={{
                        fontFamily: FF.body,
                        fontWeight: 600,
                        fontSize: 13.5,
                        padding: "12px 22px",
                        border: "1px solid rgba(255,255,255,0.3)",
                        color: "#fff",
                        textDecoration: "none",
                        borderRadius: 8,
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.65)")}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)")}
                    >
                      Call Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </>
  );
};

export default ProjectOneProductDetailPage;